import { useState } from 'react';
import { LandInspection as InspectionReport, MediaItem, PERMISSIONS } from '@property/shared';
import { usePermissions } from '@/hooks/use-permissions';
import { useUserProfile } from '@/hooks/use-task-queries';
import { useReportPdf } from '@/hooks/use-inspection-queries';
import { Button } from '@/components/ui/button';
import { Download, Calendar, User, CloudSun, Loader2, UserCheck, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageViewer } from '@/components/image-viewer';
import { ReportFeedbackDialog } from '@/components/report-feedback-dialog';
import { ReviewActionsSection, ReviewNotesSection } from '@/components/report-review-actions';
import { OwnershipCard } from '@/components/report-ownership-card';
import { ObservationsCard } from '@/components/report-observations-card';
import { SiteConditionsGrid } from '@/components/report-site-conditions';
import { IssuesList } from '@/components/report-issues-list';
import { SiteMediaSection } from '@/components/report-site-media';
import { ViewJumpNav, type JumpLink } from '@/components/view-jump-nav';
import { appUrls } from '@/lib/app-urls';

const STATUS_STAMPS: Record<string, { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'border-sidebar-primary text-sidebar-primary' },
  pending_review: { label: 'Pending', className: 'border-amber-600 text-amber-700' },
  flagged: { label: 'Flagged', className: 'border-ring text-ring' },
  rejected: { label: 'Rejected', className: 'border-ring text-ring' },
};

function StatusStamp({ status }: { status: string }) {
  const stamp = STATUS_STAMPS[status];
  if (!stamp) return null;
  return (
    <span
      className={cn(
        'inline-block rotate-[-4deg] rounded-sm border-2 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]',
        stamp.className,
      )}
    >
      {stamp.label}
    </span>
  );
}

interface ReportDetailViewProps {
  report: InspectionReport;
}

export function ReportDetailView({ report }: ReportDetailViewProps) {
  const { can } = usePermissions();
  const isPending = report.status === 'pending_review';
  const canReview = can(PERMISSIONS.INSPECTIONS_REVIEW) && isPending;
  const { data: inspectorUser } = useUserProfile(report.inspector);
  const { data: reviewerUser } = useUserProfile(report.reviewedBy);

  const jumpLinks: JumpLink[] = can(PERMISSIONS.PROPERTIES_READ)
    ? [{ label: 'Property', icon: MapPin, to: appUrls.propertyMonitoring(report.propertyId) }]
    : [];

  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [editableSummary, setEditableSummary] = useState(report.summary);
  const { mutate: downloadPdf, isPending: isPdfLoading } = useReportPdf();

  function handleDownloadPdf() {
    downloadPdf(report.inspectionId);
  }

  return (
    <div className="space-y-10 pb-12">
      {/* Document header */}
      <div className="border-b border-dashed border-border-strong pb-6">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-sidebar-primary">
              Field inspection report · {report.inspectionId}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <h1 className="font-display text-3xl font-medium tracking-tight text-foreground">
                Inspection Report
              </h1>
              <StatusStamp status={report.status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground/60">
              <span className="flex items-center gap-1.5">
                <Calendar className="size-4 opacity-70" /> {report.visitDate}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="size-4 opacity-70" /> {inspectorUser?.name ?? report.inspector}
              </span>
              <span className="flex items-center gap-1.5">
                <CloudSun className="size-4 opacity-70" /> {report.weather}
              </span>
              {report.reviewedBy && (
                <span className="flex items-center gap-1.5">
                  <UserCheck className="size-4 opacity-70" />
                  {reviewerUser?.name ?? report.reviewedBy}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ReportFeedbackDialog inspectionId={report.inspectionId} />
            <Button onClick={handleDownloadPdf} disabled={isPdfLoading} className="h-10 px-6">
              {isPdfLoading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Download className="mr-2 size-4" />
              )}
              {isPdfLoading ? 'Generating…' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </div>

      <ViewJumpNav links={jumpLinks} />

      <div className="space-y-10">
        <OwnershipCard report={report} onMediaSelect={setSelectedMedia} />
        <SiteMediaSection
          mainAccessPoint={report.mainAccessPoint}
          siteOverview={report.siteOverview}
          onMediaSelect={setSelectedMedia}
        />
        <SiteConditionsGrid report={report} onMediaSelect={setSelectedMedia} />
        <IssuesList issues={report.issues ?? []} />
        <ObservationsCard
          summary={editableSummary}
          status={report.status}
          onSummaryChange={canReview ? setEditableSummary : undefined}
        />
      </div>

      {!isPending && report.reviewNotes && can(PERMISSIONS.INSPECTIONS_REVIEW) && (
        <ReviewNotesSection notes={report.reviewNotes} reviewedAt={report.reviewedAt} />
      )}
      {canReview && (
        <ReviewActionsSection inspectionId={report.inspectionId} summary={editableSummary} />
      )}

      <ImageViewer media={selectedMedia} onClose={() => setSelectedMedia(null)} />
    </div>
  );
}
